import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/img/logo-header.png';
import LeftSideBarItem from "./LeftSideBarItem";
import LeftSideBarParentItem from "./LeftSideBarParentItem";
import { useSelector, useDispatch } from 'react-redux'
import { menuToggle ,activated} from "../../reducer"
import axios from 'axios';
import config from "../../config.js";

const LeftSideBar = ({ onHover }) => {
    const toggle = useSelector((state) => state.menuToggle?.toggle)
    const minimize = useSelector((state) => state.menuToggle?.minimize)
    const active = useSelector((state) => state.active?.active) || {}
    const dispatch = useDispatch()
    const location = useLocation();
    
    const reduxLogo = useSelector((state) => state.clientLogo?.url);
    const ClientLogo = (reduxLogo && reduxLogo !== '') ? reduxLogo : localStorage.getItem('ClientLogo');
    const reduxSiteClientLogo = useSelector((state) => state.siteClientLogo?.url);
    const SiteClientLogo = (reduxSiteClientLogo && reduxSiteClientLogo !== '') ? reduxSiteClientLogo : localStorage.getItem('SiteClientLogo');
    const SystemModules = useMemo(() => JSON.parse(localStorage.getItem('SystemModules')), []);
    
    const reduxProfile = useSelector((state) => state.userProfile?.data);
    const user = useMemo(() => {
        if (reduxProfile) return reduxProfile;
        try {
            const cached = JSON.parse(localStorage.getItem('userProfile'));
            if (cached) return cached;
        } catch {}
        const role = localStorage.getItem('userRole');
        return role ? { role: Number(role) } : {};
    }, [reduxProfile]);

    // Build a map of child id -> parent id to preserve ancestor chains when toggling nested parents
    const parentMap = useMemo(() => {
        const map = {};
        const walk = (item, parentId = null) => {
            if (!item || typeof item !== 'object') return;
            if (typeof item.id !== 'undefined') map[item.id] = parentId;
            if (Array.isArray(item.child)) item.child.forEach(ch => walk(ch, item.id));
        };
        if (Array.isArray(SystemModules)) SystemModules.forEach(root => walk(root, null));
        return map;
    }, [SystemModules]);

    const getAncestorSet = useCallback((id) => {
        const set = new Set();
        let cur = id;
        while (typeof cur !== 'undefined' && cur !== null) {
            set.add(String(cur));
            cur = parentMap[cur];
        }
        return set;
    }, [parentMap]);

    const getParentChain = useCallback((id) => {
        const out = [];
        let p = parentMap[id];
        while (typeof p !== 'undefined' && p !== null) {
            out.push(String(p));
            p = parentMap[p];
        }
        return out;
    }, [parentMap]);

    const defaultActive = useCallback((MenuItem, all) => {
        if (Array.isArray(MenuItem.child)) {
            all[MenuItem.id] = ""
            MenuItem.child.forEach((MenuItemNew) => {
                defaultActive(MenuItemNew, all);
            })
        }
    }, []);

    useEffect(() => {
        if (!JSON.parse(localStorage.getItem("redux_menu_active"))) {
            let all = {};
            SystemModules.forEach((MenuItem) => {
                defaultActive(MenuItem, all);
            });
            dispatch(activated(all))
        }
    }, [SystemModules, defaultActive, dispatch]);

    // Auto-open the parent group(s) based on current route path
    useEffect(() => {
        if (!Array.isArray(SystemModules)) return;
        const pathname = location.pathname || '/';
        // Helper: recursively find ancestor id chain for the current route
        const findAncestors = (item, targetPath) => {
            const itemPath = item.route ? '/' + String(item.route).replace(/^\//,'') : null;
            if (itemPath && (targetPath === itemPath || targetPath.startsWith(itemPath + '/'))) {
                return [item.id];
            }
            if (Array.isArray(item.child)) {
                for (const child of item.child) {
                    const found = findAncestors(child, targetPath);
                    if (found && found.length) return [item.id, ...found];
                }
            }
            return [];
        };
        let ancestors = [];
        for (const top of SystemModules) {
            ancestors = findAncestors(top, pathname);
            if (ancestors.length) break;
        }
        if (ancestors.length) {
            // Build a new active map: open all ancestors, close others (exclusive)
            const next = { ...active };
            Object.keys(next).forEach(k => { next[k] = ''; });
            ancestors.forEach(id => { if (typeof id !== 'undefined') next[id] = 'show'; });
            dispatch(activated(next));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Ensure page-level classes exist so aside CSS rules apply without JS framework
    useEffect(() => {
        const b = document.body;
        b.classList.add('aside-enabled','aside-fixed');
        return () => {
            // don't remove on unmount to keep layout consistent across pages
        };
    }, []);

    const activeSideBar = useCallback((index) => {
        const idx = String(index);
        const wasOpen = active && active[idx] === 'show';
        const next = { ...active };
        
        // Close everything first
        Object.keys(next).forEach(k => { next[k] = ''; });
        
        // Keep parent chain open
        const parents = getParentChain(index);
        parents.forEach(p => { next[p] = 'show'; });
        
        // Toggle clicked node
        if (!wasOpen) {
            next[idx] = 'show';
        }
        
        dispatch(activated(next));
    }, [active, getParentChain, dispatch]);

    const isMobile = () => window.innerWidth < 992;

    // On mobile the expanded sidebar is an overlay: collapse it once a page is chosen
    useEffect(() => {
        if (window.innerWidth < 992 && minimize === 'off') {
            dispatch(menuToggle());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleMouseEnter = () => {
        if (isMobile()) return;
        if (minimize === 'on' && onHover) {
            onHover(true);
        }
    };

    const handleMouseLeave = () => {
        if (isMobile()) return;
        if (minimize === 'on' && onHover) {
            onHover(false);
        }
    };


    return (
        <>
        <div className="aside-overlay" onClick={() => {
            if (minimize === 'off') dispatch(menuToggle());
        }}></div>
        <aside id="kt_aside" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="aside aside-light" data-kt-drawer="true"
            data-kt-drawer-name="aside" data-kt-drawer-activate='{"default": true, "lg": true}'
            data-kt-drawer-overlay="true" data-kt-drawer-width='{"default":"200px", "300px": "250px"}'
            data-kt-drawer-direction="start" aria-label="Sidebar Navigation">
            <div className="aside-logo flex-column-auto border-bottom" id="kt_aside_logo">
                <NavLink className="d-flex align-items-center justify-content-between px-3 py-2" to={'/'}>
                    <img alt="Logo" src={logo} className="logo" style={{flex:'0 0 auto', maxWidth:'70px', objectFit:'contain'}}/>
                    {(ClientLogo || SiteClientLogo) && (
                        <div className="d-flex align-items-center" style={{flex:'1', justifyContent:'space-evenly'}}>
                            {ClientLogo && (
                                <img
                                    alt="ClientLogo"
                                    src={ClientLogo}
                                    className="logo"
                                    style={{flex:'1', maxWidth:'70px', objectFit:'contain'}}
                                    onError={(e) => { e.target.style.display='none'; }}
                                />
                            )}
                            {SiteClientLogo && (
                                <img
                                    alt="SiteClientLogo"
                                    src={SiteClientLogo}
                                    className="logo"
                                    style={{flex:'1', maxWidth:'70px', objectFit:'contain'}}
                                    onError={(e) => { e.target.style.display='none'; }}
                                />
                            )}
                        </div>
                    )}
                </NavLink>
                <button
                    id="kt_aside_toggle"
                    type="button"
                    onClick={() => {
                        // Drive minimize via Redux only; Template applies data-kt-aside-minimize
                        dispatch(menuToggle());
                    }}
                    className={"aside-toggle-btn " + toggle }
                    aria-expanded={toggle === 'active'}
                    aria-controls="kt_aside_menu_wrapper"
                    aria-label={minimize === 'on' ? 'Expand sidebar' : 'Collapse sidebar'}
                    title={minimize === 'on' ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {/* Modern Duotone SVG Icon */}
                    <span className={`svg-icon ${minimize === 'on' ? 'rotate-180' : ''}`}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.5" d="M14.2657 11.4343L18.45 7.25C18.8642 6.83579 18.8642 6.16421 18.45 5.75C18.0358 5.33579 17.3642 5.33579 16.95 5.75L11.4071 11.2929C11.0166 11.6834 11.0166 12.3166 11.4071 12.7071L16.95 18.25C17.3642 18.6642 18.0358 18.6642 18.45 18.25C18.8642 17.8358 18.8642 17.1642 18.45 16.75L14.2657 12.5657C13.9533 12.2533 13.9533 11.7467 14.2657 11.4343Z" fill="currentColor"/>
                            <path d="M8.2657 11.4343L12.45 7.25C12.8642 6.83579 12.8642 6.16421 12.45 5.75C12.0358 5.33579 11.3642 5.33579 10.95 5.75L5.40712 11.2929C5.01659 11.6834 5.01659 12.3166 5.40712 12.7071L10.95 18.25C11.3642 18.6642 12.0358 18.6642 12.45 18.25C12.8642 17.8358 12.8642 17.1642 12.45 16.75L8.2657 12.5657C7.95328 12.2533 7.95328 11.7467 8.2657 11.4343Z" fill="currentColor"/>
                        </svg>
                    </span>
                </button>
            </div>
            <div className="aside-menu flex-column-fluid" id="kt_aside_menu">
                <nav 
                    className="hover-scroll-overlay-y overflow-auto my-2 py-5 py-lg-8" 
                    id="kt_aside_menu_wrapper"
                    data-kt-scroll="true" 
                    data-kt-scroll-activate='{"default": false, "lg": true}'
                    data-kt-scroll-height="auto" 
                    data-kt-scroll-dependencies="#kt_aside_logo, #kt_aside_footer"
                    data-kt-scroll-wrappers="#kt_aside_menu" 
                    data-kt-scroll-offset="0" 
                    aria-label="Primary"
                >
                    <div
                        className="menu menu-column menu-title-gray-800 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-500"
                        data-kt-menu="true" role="menubar">
                         {/*<LeftSideBarItem*/}
                         {/*    MenuItem={*/}
                         {/*        {   id:0,*/}
                         {/*            icon:"bi bi-house fs-3",*/}
                         {/*            route:"dashboard",*/}
                         {/*            name_en:"Dashboard"*/}
                         {/*        }*/}
                         {/*    }/>*/}
                        {SystemModules.map(function (MenuItem, MenuItemKey) {
                            if (Array.isArray(MenuItem.child)) {
                                var count = 0 ;
                                return <LeftSideBarParentItem 
                                    MenuItem={MenuItem} 
                                    User={user} 
                                    count={count} 
                                    activeSideBar={activeSideBar} 
                                    MenuItemKey={MenuItemKey} 
                                    active={active} 
                                    key={MenuItemKey}
                                />

                            } else {
                                return MenuItem.menu === "show" 
                                    ? <LeftSideBarItem 
                                        User={user} 
                                        MenuItem={MenuItem} 
                                        key={MenuItemKey}
                                     />
                                    : null;
                            }

                        })}

                    </div>
                </nav>
            </div>
        </aside>
        </>
    );
}
export default LeftSideBar;
